import { svg, esc } from '../ui/icons.js';
import { dados } from '../state.js';
import * as Op from '../data/operacional.js';
import { usuario, ehGestao } from '../auth.js';
import { abrirFormulario } from '../ui/modal.js';

/**
 * Tarefas, Pesquisas, Base de Conhecimento e Realizado do Dia.
 *
 * Estas quatro telas eram VITRINE no protótipo: arrays fixos no código que
 * não salvavam nada (só o Realizado do Dia gravava, em localStorage e só
 * do dia corrente, no navegador de quem digitou). Agora tudo persiste e é
 * compartilhado.
 */

let aoMudarDados = () => {};

export function configurar({ onChange }) {
  aoMudarDados = onChange || (() => {});

  document.getElementById('taskFilterStatus').addEventListener('change', renderTarefas);
  document.getElementById('pesqSearch').addEventListener('input', renderPesquisas);
  document.getElementById('logAdd').addEventListener('click', adicionarRealizado);
  document.getElementById('genReport').addEventListener('click', gerarResumo);

  document.getElementById('btnNovaTarefa').addEventListener('click', formTarefa);
  document.getElementById('btnNovaPesquisa').addEventListener('click', formPesquisa);
  document.getElementById('btnNovoArtigo').addEventListener('click', formArtigo);
  document.getElementById('kbSearch').addEventListener('input', renderConhecimento);
}

/* ------------------------------------------------------------------ */
/* Tarefas                                                             */
/* ------------------------------------------------------------------ */

const TAG_PRIORIDADE = { Alta: 'red', Média: 'amber', Baixa: 'purple' };
const TAG_STATUS = {
  Pendente: 'red',
  'Em andamento': 'amber',
  'Aguardando cliente': 'purple',
  'Concluído': 'green',
};

const STATUS_TAREFA = ['Pendente', 'Em andamento', 'Aguardando cliente', 'Concluído'];

/**
 * Tarefas.
 *
 * A tabela desenhava as linhas e não tinha ação nenhuma: dava para criar
 * tarefa e nunca concluir. A função de atualizar existia na camada de
 * dados e nunca tinha sido chamada por uma tela — tarefa nascia e não
 * morria.
 *
 * Agora o status é um campo que se troca na própria linha, sem abrir
 * nada: concluir é o gesto mais frequente e não devia custar dois
 * cliques. Editar e excluir ficam ao lado.
 *
 * Quem exclui continua sendo quem criou, ou a gestão — decisão dela:
 * tarefa designada pela gestão não some pela mão de quem recebeu.
 */
export function renderTarefas() {
  const filtro = document.getElementById('taskFilterStatus').value;
  const lista = dados.tarefas.filter((t) => !filtro || t.status === filtro);
  const u = usuario();

  const abertas = dados.tarefas.filter((t) => t.status !== 'Concluído').length;
  document.getElementById('taskCountSub').textContent =
    `${lista.length} de ${dados.tarefas.length} tarefas · ${abertas} em aberto`;

  const vencida = (t) => t.prazo && t.status !== 'Concluído'
    && t.prazo < new Date().toISOString().slice(0, 10);

  document.querySelector('#taskTable tbody').innerHTML = lista.length
    ? lista.map((t) => {
        const podeExcluir = ehGestao() || t.criado_por === u.id;
        return `
        <tr data-id="${t.id}" class="${t.status === 'Concluído' ? 'tarefa-feita' : ''}">
          <td>${esc(t.titulo)}</td>
          <td>${esc(t.empresa?.nome || '—')}</td>
          <td>${esc(t.responsavel?.nome || '—')}</td>
          <td class="${vencida(t) ? 'tarefa-vencida' : ''}">
            ${t.prazo ? new Date(t.prazo + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}
          </td>
          <td><span class="tag ${TAG_PRIORIDADE[t.prioridade]}">${t.prioridade}</span></td>
          <td>
            <select class="tarefa-status ${TAG_STATUS[t.status]}" data-id="${t.id}">
              ${STATUS_TAREFA.map((s) => `<option ${s === t.status ? 'selected' : ''}>${s}</option>`).join('')}
            </select>
          </td>
          <td class="tarefa-acoes">
            <button type="button" class="tarefa-editar" data-id="${t.id}" title="Editar">Editar</button>
            ${podeExcluir ? `<button type="button" class="tarefa-excluir" data-id="${t.id}" title="Excluir">Excluir</button>` : ''}
          </td>
        </tr>`;
      }).join('')
    : `<tr><td colspan="7" style="color:var(--text-muted);">Nenhuma tarefa com esse filtro.</td></tr>`;

  ligarAcoesTarefa();
}

function ligarAcoesTarefa() {
  const corpo = document.querySelector('#taskTable tbody');

  corpo.querySelectorAll('.tarefa-status').forEach((sel) =>
    sel.addEventListener('change', async () => {
      const antes = dados.tarefas.find((t) => t.id === sel.dataset.id)?.status;
      sel.disabled = true;
      try {
        await Op.atualizarTarefa(sel.dataset.id, { status: sel.value });
        dados.tarefas = await Op.listarTarefas();
        renderTarefas();
        aoMudarDados();
      } catch (erro) {
        sel.value = antes;
        sel.disabled = false;
        alert('Não foi possível salvar: ' + erro.message);
      }
    })
  );

  corpo.querySelectorAll('.tarefa-editar').forEach((b) =>
    b.addEventListener('click', () => formTarefa(dados.tarefas.find((t) => t.id === b.dataset.id)))
  );

  corpo.querySelectorAll('.tarefa-excluir').forEach((b) =>
    b.addEventListener('click', async () => {
      const t = dados.tarefas.find((x) => x.id === b.dataset.id);
      if (!t) return;
      if (!confirm(`Excluir a tarefa "${t.titulo}"?\n\nIsso não pode ser desfeito.`)) return;
      b.disabled = true;
      try {
        await Op.removerTarefa(t.id);
        dados.tarefas = await Op.listarTarefas();
        renderTarefas();
        aoMudarDados();
      } catch (erro) {
        b.disabled = false;
        alert('Não foi possível excluir: ' + erro.message);
      }
    })
  );
}

/* ------------------------------------------------------------------ */
/* Pesquisas                                                           */
/* ------------------------------------------------------------------ */

export function renderPesquisas() {
  const busca = document.getElementById('pesqSearch').value.toLowerCase();
  const lista = dados.pesquisas.filter((p) =>
    (p.assunto + (p.empresa?.nome || '') + p.conclusao).toLowerCase().includes(busca)
  );

  const u = usuario();

  document.querySelector('#pesqTable tbody').innerHTML = lista.length
    ? lista.map((p) => {
        // Corrigir a própria conclusão é o caso comum: a pessoa registra
        // no calor do problema e descobre um detalhe depois.
        const podeEditar = ehGestao() || p.autor?.id === u.id;
        return `
        <tr>
          <td><b>${esc(p.assunto)}</b></td>
          <td>${esc(p.empresa?.nome || '—')}</td>
          <td style="max-width:280px;">${esc(p.conclusao)}</td>
          <td>${esc(p.fonte || '—')}</td>
          <td>${p.data ? new Date(p.data + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}</td>
          <td>${esc(p.autor?.nome || '—')}</td>
          <td class="tarefa-acoes">
            ${podeEditar ? `<button type="button" class="pesq-editar" data-id="${p.id}">Editar</button>` : ''}
          </td>
        </tr>`;
      }).join('')
    : `<tr><td colspan="7" style="color:var(--text-muted);">Nenhuma pesquisa registrada${busca ? ' com esse termo' : ' ainda'}.</td></tr>`;

  document.querySelectorAll('.pesq-editar').forEach((b) =>
    b.addEventListener('click', () => formPesquisa(dados.pesquisas.find((p) => p.id === b.dataset.id)))
  );
}

/* ------------------------------------------------------------------ */
/* Base de conhecimento                                                */
/* ------------------------------------------------------------------ */

/*
 * Base de conhecimento.
 *
 * A tela listava os títulos e clicar não fazia nada — a pessoa via
 * "Manual ECD" e não tinha como ler. Com 20 manuais e 109 mil caracteres
 * importados, listar título é quase inútil: quem precisa saber "como
 * lançar a integração da folha" não sabe em qual manual isso está.
 *
 * Agora busca no TEXTO, não só no título, e mostra o conteúdo ao lado com
 * o trecho encontrado destacado.
 */
let artigoAberto = null;

function todosArtigos() {
  return dados.kb.flatMap((cat) => (cat.artigos || []).map((a) => ({ ...a, categoria: cat.nome })));
}

/**
 * Sugestões esperando decisão da gestão.
 *
 * Sem esta faixa, o "Sugerir correção" do colaborador cairia num lugar
 * que ninguém abre — o mesmo erro que a correção da revisão cometia antes
 * de entrar no "Precisa de atenção".
 */
async function renderSugestoes() {
  const alvo = document.getElementById('kbSugestoes');
  if (!alvo) return;
  if (!ehGestao()) { alvo.hidden = true; return; }

  let lista = [];
  try { lista = await Op.sugestoesAbertas(); } catch { lista = []; }

  alvo.hidden = !lista.length;
  if (!lista.length) return;

  alvo.innerHTML = `
    <b>${lista.length} sugestão(ões) de correção esperando você</b>
    ${lista.map((s) => `
      <div class="kb-sugestao">
        <span class="kb-sugestao-qual">${esc(s.artigo_titulo || s.titulo_sugerido || 'artigo novo')}</span>
        <span>${esc(s.texto)}</span>
        <span class="kb-sugestao-quem">${esc(s.solicitante)}</span>
      </div>`).join('')}`;
}

export function renderConhecimento() {
  const busca = (document.getElementById('kbSearch')?.value || '').trim().toLowerCase();
  const wrap = document.getElementById('kbWrap');
  renderSugestoes();

  const casa = (a) =>
    !busca ||
    a.titulo.toLowerCase().includes(busca) ||
    (a.conteudo || '').toLowerCase().includes(busca);

  const categorias = dados.kb
    .map((cat) => ({ ...cat, artigos: (cat.artigos || []).filter(casa) }))
    .filter((cat) => cat.artigos.length || !busca);

  const achados = categorias.reduce((n, c) => n + c.artigos.length, 0);
  const total = todosArtigos().length;
  const sub = document.getElementById('kbSub');
  if (sub) {
    sub.textContent = busca
      ? `${achados} de ${total} manuais mencionam "${busca}"`
      : `${total} manuais e procedimentos internos.`;
  }

  wrap.innerHTML = categorias.length
    ? categorias.map((cat) => `
        <div class="kb-cat">
          <h3>${esc(cat.nome)}</h3>
          ${cat.artigos.length
            ? cat.artigos.map((a) => `
                <div class="kb-item ${a.id === artigoAberto ? 'aberto' : ''}" data-id="${a.id}">
                  <span>${esc(a.titulo)}</span><span class="chev">${svg('chev', 15)}</span>
                </div>`).join('')
            : `<div class="kb-item" style="color:var(--text-muted);cursor:default;"><span>Nenhum artigo nesta categoria ainda.</span></div>`}
        </div>`).join('')
    : `<p style="font-size:13px;color:var(--text-muted);padding:4px;">
         Nenhum manual menciona "${esc(busca)}". Tente outra palavra.</p>`;

  wrap.querySelectorAll('.kb-item[data-id]').forEach((el) =>
    el.addEventListener('click', () => abrirArtigo(el.dataset.id, busca))
  );

  if (artigoAberto && !todosArtigos().some((a) => a.id === artigoAberto)) artigoAberto = null;
  if (artigoAberto) abrirArtigo(artigoAberto, busca);
  else renderLeitorVazio(busca);
}

function renderLeitorVazio(busca) {
  document.getElementById('kbLeitor').innerHTML = `
    <p style="font-size:13px;color:var(--text-muted);margin:0;">
      ${busca
        ? 'Escolha um manual à esquerda para ler o trecho.'
        : 'Escolha um manual à esquerda, ou busque por uma palavra do procedimento — a busca olha o texto inteiro, não só o título.'}
    </p>`;
}

/** Destaca a busca sem deixar passar HTML do conteúdo. */
function destacar(texto, busca) {
  const seguro = esc(texto);
  if (!busca) return seguro;
  const alvo = busca.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return seguro.replace(new RegExp(alvo, 'gi'), (m) => `<mark>${m}</mark>`);
}

function abrirArtigo(id, busca = '') {
  const a = todosArtigos().find((x) => x.id === id);
  if (!a) return;
  artigoAberto = id;

  document.querySelectorAll('#kbWrap .kb-item').forEach((el) =>
    el.classList.toggle('aberto', el.dataset.id === id)
  );

  // O rodapé com o caminho do arquivo é mostrado à parte: é o que resolve
  // quando o procedimento diz "conforme a imagem abaixo" e a imagem não
  // veio no texto.
  const corte = (a.conteudo || '').lastIndexOf('\n---\nArquivo original:');
  const corpo = corte > 0 ? a.conteudo.slice(0, corte) : (a.conteudo || '');
  const origem = corte > 0 ? a.conteudo.slice(corte + 5) : '';

  /*
   * Editar é só da gestão, por decisão dela: "senão eu vou começar a
   * perder as minhas bases". Em troca o colaborador tem como avisar —
   * sem esse canal, quem achasse erro num manual simplesmente não
   * avisaria, e o erro ficaria lá.
   */
  document.getElementById('kbLeitor').innerHTML = `
    <div class="kb-leitor-topo">
      <div>
        <h3>${esc(a.titulo)}</h3>
        <p class="kb-leitor-cat">${esc(a.categoria)}</p>
      </div>
      <div class="tarefa-acoes">
        ${ehGestao()
          ? `<button type="button" class="kb-editar" id="kbEditar">Editar</button>`
          : `<button type="button" class="kb-editar" id="kbSugerir">Sugerir correção</button>`}
        <button type="button" class="kb-fechar" id="kbFechar">Fechar</button>
      </div>
    </div>
    <div class="kb-texto">${corpo.trim()
      ? destacar(corpo, busca)
      : '<span style="color:var(--text-muted);">Este artigo ainda não tem texto. Use “Novo artigo” para escrever, ou importe o manual da rede.</span>'}</div>
    ${origem ? `<p class="kb-origem">${esc(origem)}</p>` : ''}`;

  document.getElementById('kbFechar').addEventListener('click', () => {
    artigoAberto = null;
    renderConhecimento();
  });

  document.getElementById('kbEditar')?.addEventListener('click', () => formArtigo(a));
  document.getElementById('kbSugerir')?.addEventListener('click', () => formSugestao(a));

  // Leva a primeira ocorrência para a área visível.
  const marca = document.querySelector('#kbLeitor mark');
  if (marca) marca.scrollIntoView({ block: 'center' });
}

/* ------------------------------------------------------------------ */
/* Realizado do dia                                                    */
/* ------------------------------------------------------------------ */

/*
 * O dia da pessoa tem duas metades.
 *
 * Pergunta dela: "a pessoa vai preencher o checklist e vai preencher lá
 * de novo? Minha intenção é que, conforme ela preenche o checklist, já
 * apareça no realizado. Agora, se fez alguma atividade que não é do
 * checklist — consultoria, treinamento — aí sim ela escreve."
 *
 * É isso. A metade de cima é lida das marcações e ninguém digita; a de
 * baixo continua sendo escrita à mão, para o que não tem onde ser
 * marcado.
 */

const dataEscolhida = () =>
  document.getElementById('logData')?.value || new Date().toISOString().slice(0, 10);

const pessoaEscolhida = () =>
  document.getElementById('logPessoa')?.value || usuario().id;

export function configurarRealizado() {
  const campoData = document.getElementById('logData');
  if (campoData && !campoData.value) campoData.value = new Date().toISOString().slice(0, 10);
  campoData?.addEventListener('change', recarregarRealizado);

  // A gestão precisa ver o dia da equipe; o colaborador vê o próprio.
  // A função no banco recusa o contrário, então a tela não promete nada
  // que o banco fosse negar.
  const pessoa = document.getElementById('logPessoa');
  if (pessoa && ehGestao()) {
    pessoa.hidden = false;
    pessoa.innerHTML = dados.colaboradores
      .map((c) => `<option value="${c.id}" ${c.id === usuario().id ? 'selected' : ''}>${esc(c.nome)}</option>`)
      .join('');
    pessoa.addEventListener('change', recarregarRealizado);
  }
}

async function recarregarRealizado() {
  dados.realizado = await Op.listarRealizado({ data: dataEscolhida() });
  renderRealizado();
}

export async function renderRealizado() {
  renderManuais();
  await renderAutomatico();
}

function renderManuais() {
  const quem = pessoaEscolhida();
  const meus = dados.realizado.filter((l) => l.colaborador?.id === quem);
  const meu = quem === usuario().id;

  document.getElementById('logCount').textContent = `${meus.length} registro(s)`;
  document.getElementById('logList').innerHTML = meus.length
    ? meus.map((l) => `
        <div class="log-entry">
          <span class="t">${l.hora?.slice(0, 5) ?? ''}</span>
          <span style="flex:1;"><b>${esc(l.empresa?.nome || l.empresa_texto || '—')}</b> — ${esc(l.atividade)}</span>
          <span class="tag ${l.status === 'Concluído' ? 'green' : 'amber'}">${l.status}</span>
          ${meu || ehGestao()
            ? `<button type="button" class="log-apagar" data-log="${l.id}" title="Apagar registro">${svg('trash', 14)}</button>`
            : ''}
        </div>`).join('')
    : `<p class="log-vazio">Nada escrito à mão ${meu ? 'por você ' : ''}neste dia.</p>`;

  document.querySelectorAll('.log-apagar').forEach((b) =>
    b.addEventListener('click', () => apagarRealizado(b.dataset.log))
  );
}

async function apagarRealizado(id) {
  if (!confirm('Apagar este registro?')) return;
  try {
    await Op.removerRealizado(id);
    dados.realizado = await Op.listarRealizado({ data: dataEscolhida() });
    renderManuais();
    aoMudarDados();
  } catch (err) {
    alert('Não foi possível apagar: ' + err.message);
  }
}

async function renderAutomatico() {
  const alvo = document.getElementById('logAuto');
  if (!alvo) return;
  alvo.innerHTML = '<p class="log-vazio">Lendo as marcações…</p>';

  let linhas;
  try {
    linhas = await Op.realizadoDoDia({ data: dataEscolhida(), colaboradorId: pessoaEscolhida() });
  } catch (err) {
    alvo.innerHTML = `<p class="log-vazio">Não consegui ler: ${esc(err.message)}</p>`;
    return;
  }

  document.getElementById('logAutoCount').textContent = `${linhas.length} linha(s)`;

  if (!linhas.length) {
    alvo.innerHTML = '<p class="log-vazio">Nenhuma marcação neste dia.</p>';
    return;
  }

  // Uma empresa por bloco: é assim que o recado é dado.
  const porEmpresa = new Map();
  for (const l of linhas) {
    if (!porEmpresa.has(l.empresa)) porEmpresa.set(l.empresa, []);
    porEmpresa.get(l.empresa).push(l);
  }

  const cor = (d) =>
    d?.startsWith('concluiu') ? 'green'
    : d?.startsWith('deixou') ? 'amber'
    : d?.startsWith('voltou') ? 'red' : 'blue';

  alvo.innerHTML = [...porEmpresa.entries()].map(([empresa, itens]) => `
    <div class="log-auto-empresa">
      <h4>${esc(empresa)}</h4>
      ${itens.map((l) => `
        <div class="log-auto-linha">
          <span class="t">${(l.primeira_hora || '').slice(0, 5)}</span>
          <span class="log-auto-frente">${esc(l.frente)}</span>
          <span class="tag ${l.origem === 'observacao' ? 'purple' : cor(l.detalhe)}">${esc(l.detalhe)}</span>
          <span class="log-auto-comp">${esc(l.competencias || '')}</span>
        </div>`).join('')}
    </div>`).join('');
}

async function adicionarRealizado() {
  const hora = document.getElementById('logHora').value || new Date().toTimeString().slice(0, 5);
  const textoEmpresa = document.getElementById('logEmpresa').value.trim();
  const atividade = document.getElementById('logAtividade').value.trim();
  const status = document.getElementById('logStatus').value;
  if (!textoEmpresa || !atividade) return;

  // Se o que foi digitado casa com uma empresa cadastrada, vincula de verdade.
  const empresa = dados.empresas.find(
    (e) => e.nome.toLowerCase() === textoEmpresa.toLowerCase()
  );

  try {
    await Op.registrarRealizado({
      hora,
      empresa_id: empresa?.id ?? null,
      empresa_texto: empresa ? null : textoEmpresa,
      atividade,
      status,
    }, usuario().id);

    document.getElementById('logEmpresa').value = '';
    document.getElementById('logAtividade').value = '';
    document.getElementById('logHora').value = '';

    dados.realizado = await Op.listarRealizado({ data: dataEscolhida() });
    renderRealizado();
    aoMudarDados();
  } catch (err) {
    alert('Não foi possível registrar a atividade: ' + err.message);
  }
}

function gerarResumo() {
  const meus = dados.realizado.filter((l) => l.colaborador?.id === usuario().id);
  const empresas = [...new Set(meus.map((l) => l.empresa?.nome || l.empresa_texto))].filter(Boolean);
  const pendentes = meus.filter((l) => l.status === 'Pendente').length;

  const texto =
    `Resumo do dia — ${new Date().toLocaleDateString('pt-BR')}\n` +
    `${usuario().nome}\n\n` +
    `Atividades registradas: ${meus.length}\n` +
    `Empresas trabalhadas: ${empresas.length ? empresas.join(', ') : '—'}\n` +
    `Concluídas: ${meus.length - pendentes}\n` +
    `Pendentes: ${pendentes}\n\n` +
    meus.map((l) =>
      `${l.hora?.slice(0, 5)} — ${l.empresa?.nome || l.empresa_texto} — ${l.atividade} — ${l.status}`
    ).join('\n');

  const caixa = document.getElementById('summaryBox');
  caixa.textContent = texto;
  caixa.style.display = 'block';
}

/* ------------------------------------------------------------------ */
/* Formulários                                                         */
/*                                                                     */
/* Estas três telas nasceram somente-leitura no protótipo. Sem os       */
/* formulários abaixo não havia como alimentar nem as tarefas, nem as   */
/* pesquisas, nem a base de conhecimento.                               */
/* ------------------------------------------------------------------ */

const opcoesEmpresa = () => [
  { valor: '', rotulo: '— sem empresa —' },
  ...dados.empresas.map((e) => ({ valor: e.id, rotulo: e.nome })),
];

/** O mesmo formulário cria e edita: os campos são os mesmos. */
function formTarefa(tarefa = null) {
  abrirFormulario({
    titulo: tarefa ? 'Editar tarefa' : 'Nova tarefa',
    campos: [
      { id: 'titulo', label: 'Tarefa', tipo: 'texto', obrigatorio: true, valor: tarefa?.titulo },
      { id: 'empresa_id', label: 'Empresa', tipo: 'select', opcoes: opcoesEmpresa(), valor: tarefa?.empresa?.id },
      { id: 'responsavel_id', label: 'Responsável', tipo: 'select',
        valor: tarefa?.responsavel?.id ?? usuario().id,
        opcoes: dados.colaboradores.map((c) => ({ valor: c.id, rotulo: c.nome })) },
      { id: 'prazo', label: 'Prazo', tipo: 'data', valor: tarefa?.prazo },
      { id: 'prioridade', label: 'Prioridade', tipo: 'select', valor: tarefa?.prioridade ?? 'Média',
        opcoes: ['Alta', 'Média', 'Baixa'].map((p) => ({ valor: p, rotulo: p })) },
      { id: 'status', label: 'Status', tipo: 'select', valor: tarefa?.status ?? 'Pendente',
        opcoes: STATUS_TAREFA.map((s) => ({ valor: s, rotulo: s })) },
    ],
    aoSalvar: async (v) => {
      if (tarefa) await Op.atualizarTarefa(tarefa.id, v);
      else await Op.criarTarefa(v, usuario().id);
      dados.tarefas = await Op.listarTarefas();
      renderTarefas();
      aoMudarDados();
    },
  });
}

function formPesquisa(pesquisa = null) {
  abrirFormulario({
    titulo: pesquisa ? 'Editar pesquisa' : 'Registrar pesquisa',
    subtitulo: 'O que você descobriu fica disponível para a equipe inteira — é o que evita refazer a mesma consulta.',
    campos: [
      { id: 'assunto', label: 'Assunto', tipo: 'texto', obrigatorio: true, valor: pesquisa?.assunto,
        dica: 'Escreva como você buscaria daqui a seis meses.' },
      { id: 'empresa_id', label: 'Empresa', tipo: 'select', opcoes: opcoesEmpresa(), valor: pesquisa?.empresa?.id },
      { id: 'conclusao', label: 'Conclusão', tipo: 'area', obrigatorio: true, valor: pesquisa?.conclusao,
        dica: 'A resposta objetiva, não o caminho até ela.' },
      { id: 'fonte', label: 'Fonte', tipo: 'texto', valor: pesquisa?.fonte,
        dica: 'e-CAC, Receita Federal, IOB, manual interno...' },
      { id: 'data', label: 'Data', tipo: 'data',
        valor: pesquisa?.data ?? new Date().toISOString().slice(0, 10) },
    ],
    aoSalvar: async (v) => {
      if (pesquisa) await Op.atualizarPesquisa(pesquisa.id, v);
      else await Op.criarPesquisa(v, usuario().id);
      dados.pesquisas = await Op.listarPesquisas();
      renderPesquisas();
      aoMudarDados();
    },
  });
}

function formArtigo(artigo = null) {
  const categoriaAtual = artigo
    ? dados.kb.find((c) => (c.artigos || []).some((x) => x.id === artigo.id))?.id
    : null;

  abrirFormulario({
    titulo: artigo ? 'Editar artigo' : 'Novo artigo',
    subtitulo: 'Procedimento interno, para quem pegar essa situação da próxima vez.',
    campos: [
      { id: 'categoria_id', label: 'Categoria', tipo: 'select', obrigatorio: true, valor: categoriaAtual,
        opcoes: dados.kb.map((c) => ({ valor: c.id, rotulo: c.nome })) },
      { id: 'titulo', label: 'Título', tipo: 'texto', obrigatorio: true, valor: artigo?.titulo },
      { id: 'conteudo', label: 'Conteúdo', tipo: 'area', obrigatorio: true, valor: artigo?.conteudo,
        dica: 'Passo a passo. Quem ler precisa conseguir executar sem perguntar.' },
    ],
    aoSalvar: async (v) => {
      const campos = { ...v, categoria_id: Number(v.categoria_id) };
      if (artigo) await Op.atualizarArtigo(artigo.id, campos);
      else await Op.criarArtigo(campos, usuario().id);
      dados.kb = await Op.baseConhecimento();
      renderConhecimento();
    },
  });
}

/**
 * Sugestão de correção num manual, para quem não é gestão.
 *
 * O pedido fica registrado com autor e data, e a gestão decide. É o outro
 * lado da restrição de edição: tirar o poder de editar sem dar um canal
 * de aviso faria a pessoa desistir de avisar.
 */
function formSugestao(artigo) {
  abrirFormulario({
    titulo: 'Sugerir correção',
    subtitulo: `Sobre o manual "${artigo.titulo}". A gestão lê e decide se aplica.`,
    campos: [
      { id: 'texto', label: 'O que está errado ou faltando', tipo: 'area', obrigatorio: true,
        dica: 'Diga o trecho e o que deveria dizer. Quanto mais preciso, mais rápido é aplicar.' },
    ],
    aoSalvar: async (v) => {
      await Op.sugerirCorrecao({ artigoId: artigo.id, texto: v.texto }, usuario().id);
      alert('Sugestão enviada. A gestão vai avaliar.');
    },
  });
}
